import React, { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useStore, useSelector } from "react-redux";
import { Persistor } from "redux-persist";

import { connectSessionSocket, disconnectSessionSocket } from "../services/sessionSocket";
import { setupSessionInterceptor } from "../Utils/axiosSessionInterceptor";

interface SessionInterceptorProviderProps {
  children: React.ReactNode;
  persistor?: Persistor;
  resetActions?: Array<() => { type: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tokenSelector: (state: any) => { token: string };
}

export const SessionInterceptorProvider: React.FC<SessionInterceptorProviderProps> = ({
  children,
  persistor,
  resetActions,
  tokenSelector,
}) => {
  const store = useStore();
  const queryClient = useQueryClient();
  const isSetup = useRef(false);
  const { token } = useSelector(tokenSelector);

  // Store stable references to avoid re-triggering the socket effect
  const storeRef = useRef(store);
  const resetActionsRef = useRef(resetActions);
  const persistorRef = useRef(persistor);
  const queryClientRef = useRef(queryClient);
  const tokenSelectorRef = useRef(tokenSelector);

  storeRef.current = store;
  resetActionsRef.current = resetActions;
  persistorRef.current = persistor;
  queryClientRef.current = queryClient;
  tokenSelectorRef.current = tokenSelector;

  // Set up the axios interceptor once
  useEffect(() => {
    if (!isSetup.current) {
      setupSessionInterceptor({
        store,
        resetActions,
        persistor,
        queryClient,
      });
      isSetup.current = true;
    }
  }, [store, resetActions, persistor, queryClient]);

  // Connect to session WebSocket only when token changes (i.e., after login)
  useEffect(() => {
    if (!token || token === "none") {
      disconnectSessionSocket();
      return;
    }

    connectSessionSocket({
      store: storeRef.current,
      tokenSelector: tokenSelectorRef.current,
      resetActions: resetActionsRef.current,
      persistor: persistorRef.current,
      queryClient: queryClientRef.current,
    });

    return () => {
      disconnectSessionSocket();
    };
  }, [token]); // Only re-run when token actually changes

  return <>{children}</>;
};

export default SessionInterceptorProvider;
